import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import AuthContext from './FirebaseAuthContext';
import LoadingScreen from 'src/components/LoadingScreen';
import { getUserBusinesses } from 'src/models/UserCollaboration';
import {
  clearCompanyListCache,
  readCompanyListCache,
  writeCompanyListCache,
} from 'src/utils/companyListCache';
import { Redirect, useLocation } from 'react-router-dom';

const UserContext = createContext(undefined);

export const UserContextProvider = (props) => {
  const location = useLocation();
  const [userData, setUserData] = useState({
    userId: undefined,
    stripeCustomerId: undefined,
    companyID: undefined,
    companyName: undefined,
    companyEmail: undefined,
    companyPhone: undefined,
    companyActivated: undefined,
    signupCompleted: undefined,
    companyLocationHQ: undefined,
    baseCurrency: undefined,
    salesTaxAccounts: undefined,
    transactionLockedDate: undefined,
    subscriptionCanceled: undefined,
    subscriptionEndDate: undefined,
    dateCreated: undefined,
    onboardingSteps: undefined,
    primaryContactFirstName: undefined,
    primaryContactLastName: undefined,
    promoCode: undefined,
    userRole: undefined,
    userBusinesses: undefined,
    subscriptionInterval: {
      currency: 'cad',
      interval: 'month',
    },
    companyLogoUrl: undefined,
    companyLogoFileName: undefined,
    companyLogoMimeType: undefined,
  });

  const [loading, setLoading] = useState(true);
  const FirebaseAuthContext = useContext(AuthContext);

  const [redirectOptionsPage, setRedirectOptionsPage] = useState(false);
  const [isServerDown, setIsServerDown] = useState(false);

  const fetchCompany = useCallback(async () => {
    bootstrapUserContext();
  }, [FirebaseAuthContext]);

  useEffect(() => {
    // Only hit the backend once the user is verified. The backend requires a
    // verified email on every authenticated route, and an unverified user has
    // no company yet — so skip the bootstrap until emailVerified flips true
    // (which re-runs this effect via the auth-context change).
    if (
      FirebaseAuthContext.isAuthenticated &&
      FirebaseAuthContext.user?.emailVerified &&
      userData.companyID === undefined
    ) {
      fetchCompany();
    } else {
      setLoading(false);
    }
  }, [userData, FirebaseAuthContext]);

  const switchCompanyInUse = (index: number, data = null) => {
    setLoading(true);
    const companyID = data ? data[index] : userData.userBusinesses[index];

    const salesTaxArray = [];
    let subscriptionCanceled = null;
    let subscriptionEndDate = null;

    if (companyID !== null && companyID !== undefined) {
      localStorage.setItem(
        'lastAccessedBusiness',
        JSON.stringify({
          index: index,
          id: companyID.company_id,
        })
      );
      subscriptionCanceled = companyID?.activeSubscription?.canceled;
      subscriptionEndDate = companyID?.activeSubscription?.endDate;
      companyID?.salesTaxObject?.map((item) => {
        salesTaxArray.push(item);
      });
      setUserData({
        userId: FirebaseAuthContext.user.id,
        stripeCustomerId: companyID.stripeCustomerId,
        companyID: companyID.company_id,
        companyName: companyID.company_legal_name,
        companyEmail: companyID.primary_email,
        companyPhone: companyID.primary_phone,
        companyActivated: companyID.activated,
        signupCompleted: true,
        companyLocationHQ: companyID.company_location_of_headquarters,
        baseCurrency: companyID.base_currency,
        salesTaxAccounts: salesTaxArray,
        transactionLockedDate: companyID.transactionLockedDate
          ? new Date(companyID.transactionLockedDate._seconds * 1000)
          : null,
        subscriptionCanceled: subscriptionCanceled,
        subscriptionEndDate: subscriptionEndDate,
        dateCreated: new Date(companyID.date_created._seconds * 1000),
        onboardingSteps: companyID.onboardingSteps,
        primaryContactFirstName: companyID.primary_contact_first_name,
        primaryContactLastName: companyID.primary_contact_last_name,
        promoCode: companyID.promoCode,
        userRole: companyID.role,
        userBusinesses: data ? data : userData.userBusinesses,
        subscriptionInterval: companyID.subscriptionInterval,
        companyLogoUrl: companyID.companyLogoUrl,
        companyLogoFileName: companyID.companyLogoFileName,
        companyLogoMimeType: companyID.companyLogoMimeType,
      });
    } else {
      setUserData({
        userId: FirebaseAuthContext.user.id,
        stripeCustomerId: null,
        companyID: null,
        companyName: null,
        companyEmail: null,
        companyPhone: null,
        companyActivated: false,
        signupCompleted: false,
        companyLocationHQ: null,
        baseCurrency: null,
        salesTaxAccounts: null,
        transactionLockedDate: null,
        subscriptionCanceled: null,
        subscriptionEndDate: null,
        dateCreated: null,
        onboardingSteps: null,
        primaryContactFirstName: null,
        primaryContactLastName: null,
        promoCode: null,
        userRole: null,
        userBusinesses: [],
        subscriptionInterval: {
          currency: 'cad',
          interval: 'month',
        },
        companyLogoUrl: null,
        companyLogoFileName: null,
        companyLogoMimeType: null,
      });
    }
    setLoading(false);
  };

  // Reads the last-used business selection and validates it against the
  // given company list. Returns null (never throws) when the key is missing,
  // corrupt, or doesn't match the list — corrupt JSON here used to crash the
  // whole bootstrap into a stuck loading screen.
  const readLastAccessedBusiness = (
    data: any[]
  ): { index: number; id: any } | null => {
    let raw = null;
    try {
      raw = localStorage.getItem('lastAccessedBusiness');
    } catch (err) {
      return null;
    }
    if (raw == null) return null;

    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      try {
        localStorage.removeItem('lastAccessedBusiness');
      } catch (removeErr) {}
      return null;
    }

    if (
      parsed == null ||
      typeof parsed.index !== 'number' ||
      parsed.index < 0 ||
      parsed.index >= data.length ||
      parsed.id != data[parsed.index]?.company_id
    ) {
      return null;
    }
    return parsed;
  };

  const getLastUsedCompany = (data: any) => {
    setLoading(true);

    const lastSelectedBusiness = readLastAccessedBusiness(data);

    let shouldRedirect = false;

    if (lastSelectedBusiness == null) {
      shouldRedirect = true;
    } else if (
      new Date(
        data[lastSelectedBusiness.index]?.activeSubscription?.endDate
          ?._seconds * 1000
      ) < new Date() ||
      data[lastSelectedBusiness.index]?.activeSubscription?.endDate ===
        undefined
    ) {
      shouldRedirect = true;
    }

    // find the business with an active subscription
    let firstActiveBusinessIndex = 0;

    for (let i = 0; i < data.length; i++) {
      if (
        data[i].activeSubscription &&
        data[i].activeSubscription.endDate &&
        new Date(data[i].activeSubscription.endDate._seconds * 1000) >
          new Date()
      ) {
        firstActiveBusinessIndex = i;
        break;
      }
    }

    switchCompanyInUse(
      shouldRedirect ? firstActiveBusinessIndex : lastSelectedBusiness.index,
      data
    );

    if (data.length <= 1) {
      shouldRedirect = false;
    }

    setLoading(false);
    setRedirectOptionsPage(shouldRedirect);
  };

  const resetUserContext = async () => {
    setLoading(true);

    const userCompanyData = (await getUserBusinesses()).data;

    if (userCompanyData != null) {
      setIsServerDown(false);
      writeCompanyListCache(FirebaseAuthContext.user?.id, userCompanyData);
      getLastUsedCompany(userCompanyData);
    } else {
      setIsServerDown(true);
      setUserData((prev) => ({ ...prev, companyID: null }));
    }
    setLoading(false);
  };

  // App-open bootstrap: stale-while-revalidate. Render immediately from the
  // last list this user fetched, then reconcile against the fresh one. The
  // cache is a rendering hint only — the backend re-authorizes company access
  // on every request, so a business revoked since the cache was written can't
  // return any data, and the fresh list swaps it out of the UI. Mid-session
  // refreshes keep using resetUserContext so callers that await it always see
  // fresh state and never a stale flash.
  const bootstrapUserContext = async () => {
    const uid = FirebaseAuthContext.user?.id;
    const cached = readCompanyListCache(uid);

    // Render optimistically only for the unambiguous case: a valid last-used
    // selection whose cached subscription still shows as active. Billing and
    // access redirects must never run against stale data — a monthly renewal
    // makes the cached endDate lapse, and acting on it would bounce a paying
    // user to /app/pricing or switch them into a different company. Every
    // other case (first open, renewal date passed, selection mismatch) takes
    // the blocking fresh fetch, exactly like today.
    const cachedSelection = cached ? readLastAccessedBusiness(cached) : null;
    const cachedCompany =
      cached && cachedSelection ? cached[cachedSelection.index] : null;
    const cachedSubscriptionActive =
      cachedCompany?.activeSubscription?.endDate?._seconds != null &&
      new Date(cachedCompany.activeSubscription.endDate._seconds * 1000) >
        new Date();

    if (!cached || !cachedSelection || !cachedSubscriptionActive) {
      return resetUserContext();
    }

    try {
      switchCompanyInUse(cachedSelection.index, cached);
    } catch (err) {
      // A cache written by an older build can drift from what the code above
      // expects. That must never brick startup: drop the entry and boot the
      // blocking way, same as a first open.
      clearCompanyListCache(uid);
      return resetUserContext();
    }

    const userCompanyData = (await getUserBusinesses()).data;

    if (userCompanyData != null) {
      setIsServerDown(false);
      writeCompanyListCache(uid, userCompanyData);
      if (JSON.stringify(cached) !== JSON.stringify(userCompanyData)) {
        // React 17's legacy root doesn't batch setState in promise
        // continuations, so without batching the setLoading(true) inside
        // getLastUsedCompany would flush alone, unmounting the whole app
        // through the LoadingScreen and destroying in-progress form state.
        unstable_batchedUpdates(() => {
          getLastUsedCompany(userCompanyData);
        });
      }
    } else {
      // The revalidate failed (timeout, server down). Flag it so AuthGuard
      // sends the user to /upgrading, the same as the no-cache path does.
      setIsServerDown(true);
    }
  };

  return (
    <UserContext.Provider
      value={{ userData, switchCompanyInUse, resetUserContext, isServerDown }}
    >
      {loading ? <LoadingScreen /> : props.children}
      {/* "Pick a business" is an /app-shell concern. This provider wraps the
          ENTIRE router, so an unconditional Redirect here hijacks standalone
          pages — it was yanking the OAuth consent deep link
          (/mcp/consent?request=…) off-screen seconds after login, because
          logout() clears lastAccessedBusiness and multi-business accounts then
          set redirectOptionsPage on every fresh sign-in. Gate it to /app: any
          later navigation INTO the app still lands on choose-business, while
          consent, login and other standalone flows are never interrupted. */}
      {redirectOptionsPage && location.pathname.startsWith('/app') && (
        <Redirect to="/app/collaboration/choose-business/" />
      )}
    </UserContext.Provider>
  );
};

export default UserContext;
